/**
 * Parts Inventory Service - Enterprise-Grade Parts & Inventory Management
 * 
 * Comprehensive parts tracking with stock levels, reorder automation,
 * transaction history, supplier management, and multi-location support.
 * Handles 500-2000 SKUs per company with strict tenant isolation.
 * 
 * @module partsInventoryService
 * @category Inventory
 * @security CRITICAL - Enforces multi-tenant data isolation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  QueryConstraint,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type PartCategory = 
  | 'pump_parts'
  | 'engine_parts'
  | 'electrical'
  | 'plumbing'
  | 'seals_gaskets'
  | 'bearings'
  | 'filters'
  | 'valves'
  | 'fittings'
  | 'fasteners'
  | 'lubricants'
  | 'chemicals'
  | 'safety_equipment'
  | 'tools'
  | 'consumables'
  | 'other';

export type UnitOfMeasure = 
  | 'each'
  | 'box'
  | 'case'
  | 'gallon'
  | 'quart'
  | 'liter'
  | 'pound'
  | 'kilogram'
  | 'foot'
  | 'meter'
  | 'roll'
  | 'set'
  | 'pair';

export type TransactionType = 
  | 'receipt'      // Receiving from vendor
  | 'issue'        // Issued to job/equipment
  | 'return'       // Return to stock
  | 'adjustment'   // Inventory count adjustment
  | 'transfer'     // Transfer between locations
  | 'sale'         // Sold to customer
  | 'scrap'        // Damaged/discarded
  | 'reserve'      // Reserved for work order
  | 'unreserve';   // Release reservation

export interface PartLocation {
  warehouse: string;
  shelf?: string;
  bin?: string;
  zone?: string;
}

export interface Part {
  id: string;
  companyId: string;
  
  // Identification
  partNumber: string; // AUTO: PART-2025-00001
  name: string;
  description: string;
  category: PartCategory;
  manufacturer?: string;
  manufacturerPartNumber?: string;
  alternatePartNumbers?: string[];
  barcode?: string;
  
  // Inventory Levels
  quantityOnHand: number;
  quantityAllocated: number; // Reserved for work orders
  quantityAvailable: number; // Computed: onHand - allocated
  unitOfMeasure: UnitOfMeasure;
  
  // Reorder Logic
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  autoReorder: boolean;
  
  // Location
  location: PartLocation;
  secondaryLocations?: PartLocation[];
  
  // Costing
  unitCost: number;
  averageCost: number;
  lastPurchasePrice: number;
  lastPurchaseDate?: Date | Timestamp;
  sellingPrice?: number;
  markupPercentage?: number;
  
  // Supplier
  preferredVendorId?: string;
  preferredVendorName?: string;
  vendorPartNumber?: string;
  leadTimeDays?: number;
  
  // Tracking
  serialNumberTracked: boolean;
  lotNumberTracked: boolean;
  expirationDateTracked: boolean;
  
  // Compatibility
  compatibleEquipment?: string[]; // Equipment IDs
  compatibleModels?: string[];
  
  // Specifications
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: 'in' | 'cm';
  };
  specifications?: Record<string, any>;
  
  // Documentation
  photos?: string[];
  datasheets?: string[];
  notes?: string;
  
  // Status
  isActive: boolean;
  isObsolete: boolean;
  replacementPartId?: string;
  
  // Statistics
  totalIssued: number;
  totalReceived: number;
  totalValue: number; // quantityOnHand * averageCost
  turnoverRate?: number; // Annual usage / average inventory
  lastIssuedDate?: Date | Timestamp;
  lastReceivedDate?: Date | Timestamp;
  
  // Custom Fields
  customFields?: Record<string, any>;
  
  // Metadata
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
}

export interface Transaction {
  id: string;
  companyId: string;
  
  // Transaction Details
  transactionNumber: string; // AUTO: TXN-2025-00001
  type: TransactionType;
  partId: string;
  partNumber: string;
  partName: string;
  
  // Quantity & Cost
  quantity: number;
  unitCost: number;
  totalCost: number;
  unitOfMeasure: UnitOfMeasure;
  
  // Tracking Numbers
  serialNumbers?: string[];
  lotNumber?: string;
  expirationDate?: Date | Timestamp;
  
  // Source/Destination
  fromLocation?: PartLocation;
  toLocation?: PartLocation;
  
  // Related Records
  workOrderId?: string;
  maintenanceId?: string;
  equipmentId?: string;
  equipmentName?: string;
  vendorId?: string;
  vendorName?: string;
  purchaseOrderId?: string;
  invoiceNumber?: string;
  
  // Personnel
  performedBy: string;
  performedByName: string;
  approvedBy?: string;
  approvedByName?: string;
  
  // Notes
  reason?: string;
  notes?: string;
  
  // Metadata
  transactionDate: Date | Timestamp;
  createdAt: Date | Timestamp;
  createdBy: string;
}

export interface CreatePartData {
  name: string;
  description: string;
  category: PartCategory;
  manufacturer?: string;
  manufacturerPartNumber?: string;
  unitOfMeasure: UnitOfMeasure;
  unitCost: number;
  minQuantity: number;
  maxQuantity: number;
  reorderPoint: number;
  reorderQuantity: number;
  location: PartLocation;
  preferredVendorId?: string;
  preferredVendorName?: string;
  serialNumberTracked?: boolean;
  notes?: string;
}

export interface UpdatePartData {
  name?: string;
  description?: string;
  category?: PartCategory;
  unitCost?: number;
  minQuantity?: number;
  maxQuantity?: number;
  reorderPoint?: number;
  location?: PartLocation;
  preferredVendorId?: string;
  preferredVendorName?: string;
  notes?: string;
  isActive?: boolean;
  customFields?: Record<string, any>;
}

export interface CreateTransactionData {
  type: TransactionType;
  partId: string;
  quantity: number;
  unitCost: number;
  serialNumbers?: string[];
  lotNumber?: string;
  fromLocation?: PartLocation;
  toLocation?: PartLocation;
  workOrderId?: string;
  maintenanceId?: string;
  equipmentId?: string;
  equipmentName?: string;
  vendorId?: string;
  vendorName?: string;
  reason?: string;
  notes?: string;
}

export interface PartListOptions {
  category?: PartCategory;
  isActive?: boolean;
  belowMinimum?: boolean;
  search?: string;
  limit?: number;
}

export interface TransactionListOptions {
  partId?: string;
  type?: TransactionType;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

export interface InventoryStats {
  totalParts: number;
  totalValue: number;
  totalQuantity: number;
  belowMinimum: number;
  outOfStock: number;
  overstock: number;
  byCategory: Record<string, number>;
  topValueParts: Part[];
  lowStockParts: Part[];
}

// ============================================================================
// CUSTOM ERROR CLASSES
// ============================================================================

export class PartsServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'PartsServiceError';
  }
}

export class PartNotFoundError extends PartsServiceError {
  constructor(partId: string) {
    super(
      `Part not found: ${partId}`,
      'PART_NOT_FOUND',
      { partId }
    );
  }
}

export class InsufficientStockError extends PartsServiceError {
  constructor(partId: string, requested: number, available: number) {
    super(
      `Insufficient stock for part ${partId}: requested ${requested}, available ${available}`,
      'INSUFFICIENT_STOCK',
      { partId, requested, available }
    );
  }
}

export class TenantValidationError extends PartsServiceError {
  constructor(message: string) {
    super(message, 'TENANT_VALIDATION_ERROR');
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function validateCompanyContext(companyId: string): void {
  if (!companyId || companyId === 'undefined' || companyId === 'null') {
    throw new TenantValidationError('Invalid tenant context. Please log in again.');
  }
  
  if (companyId === 'superadmin') {
    throw new TenantValidationError('SuperAdmin cannot perform tenant-scoped operations directly');
  }
}

async function generatePartNumber(companyId: string): Promise<string> {
  const year = new Date().getFullYear();
  const counterRef = doc(db, 'companies', companyId, 'counters', 'parts');
  
  try {
    const counterDoc = await getDoc(counterRef);
    const currentCount = counterDoc.exists() ? (counterDoc.data().count || 0) : 0;
    const nextCount = currentCount + 1;
    
    await updateDoc(counterRef, {
      count: nextCount,
      lastUpdated: Timestamp.now()
    }).catch(async () => {
      await addDoc(collection(db, 'companies', companyId, 'counters'), {
        count: 1,
        lastUpdated: Timestamp.now()
      });
    });
    
    return `PART-${year}-${String(nextCount).padStart(5, '0')}`;
  } catch (error) {
    return `PART-${year}-${Date.now().toString().slice(-5)}`;
  }
}

async function generateTransactionNumber(companyId: string): Promise<string> {
  const year = new Date().getFullYear();
  const counterRef = doc(db, 'companies', companyId, 'counters', 'transactions');
  
  try {
    const counterDoc = await getDoc(counterRef);
    const currentCount = counterDoc.exists() ? (counterDoc.data().count || 0) : 0;
    const nextCount = currentCount + 1;
    
    await updateDoc(counterRef, {
      count: nextCount,
      lastUpdated: Timestamp.now()
    }).catch(async () => {
      await addDoc(collection(db, 'companies', companyId, 'counters'), {
        count: 1,
        lastUpdated: Timestamp.now()
      });
    });
    
    return `TXN-${year}-${String(nextCount).padStart(6, '0')}`;
  } catch (error) {
    return `TXN-${year}-${Date.now().toString().slice(-6)}`;
  }
}

function firestoreToPart(docData: any, id: string): Part {
  return {
    ...docData,
    id,
    lastPurchaseDate: docData.lastPurchaseDate?.toDate?.() || docData.lastPurchaseDate,
    lastIssuedDate: docData.lastIssuedDate?.toDate?.() || docData.lastIssuedDate,
    lastReceivedDate: docData.lastReceivedDate?.toDate?.() || docData.lastReceivedDate,
    createdAt: docData.createdAt?.toDate?.() || docData.createdAt,
    updatedAt: docData.updatedAt?.toDate?.() || docData.updatedAt
  } as Part;
}

function firestoreToTransaction(docData: any, id: string): Transaction {
  return {
    ...docData,
    id,
    expirationDate: docData.expirationDate?.toDate?.() || docData.expirationDate,
    transactionDate: docData.transactionDate?.toDate?.() || docData.transactionDate,
    createdAt: docData.createdAt?.toDate?.() || docData.createdAt
  } as Transaction;
}

// ============================================================================
// PART CRUD OPERATIONS
// ============================================================================

export async function createPart(
  data: CreatePartData,
  createdBy: string,
  createdByName: string
): Promise<string> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const partNumber = await generatePartNumber(companyId);
    const now = Timestamp.now();

    const partData = {
      companyId,
      partNumber,
      name: data.name,
      description: data.description,
      category: data.category,
      manufacturer: data.manufacturer || '',
      manufacturerPartNumber: data.manufacturerPartNumber || '',
      alternatePartNumbers: [],
      barcode: '',
      quantityOnHand: 0,
      quantityAllocated: 0,
      quantityAvailable: 0,
      unitOfMeasure: data.unitOfMeasure,
      minQuantity: data.minQuantity,
      maxQuantity: data.maxQuantity,
      reorderPoint: data.reorderPoint,
      reorderQuantity: data.reorderQuantity,
      autoReorder: false,
      location: data.location,
      secondaryLocations: [],
      unitCost: data.unitCost,
      averageCost: data.unitCost,
      lastPurchasePrice: data.unitCost,
      sellingPrice: data.unitCost * 1.2, // Default 20% markup
      markupPercentage: 20,
      preferredVendorId: data.preferredVendorId || null,
      preferredVendorName: data.preferredVendorName || null,
      serialNumberTracked: data.serialNumberTracked || false,
      lotNumberTracked: false,
      expirationDateTracked: false,
      compatibleEquipment: [],
      compatibleModels: [],
      specifications: {},
      photos: [],
      datasheets: [],
      notes: data.notes || '',
      isActive: true,
      isObsolete: false,
      totalIssued: 0,
      totalReceived: 0,
      totalValue: 0,
      customFields: {},
      createdAt: now,
      updatedAt: now,
      createdBy,
      createdByName
    };

    const partRef = await addDoc(
      collection(db, 'companies', companyId, 'parts'),
      partData
    );

    return partRef.id;

  } catch (error: any) {
    console.error('❌ Error creating part:', error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to create part',
      'CREATE_FAILED',
      { error: error.message }
    );
  }
}

export async function getPartById(
  partId: string,
  companyId?: string
): Promise<Part> {
  try {
    const effectiveCompanyId = companyId || getCompanyId();
    validateCompanyContext(effectiveCompanyId);

    const partRef = doc(db, 'companies', effectiveCompanyId, 'parts', partId);
    const partDoc = await getDoc(partRef);

    if (!partDoc.exists()) {
      throw new PartNotFoundError(partId);
    }

    const data = partDoc.data();
    
    if (data.companyId !== effectiveCompanyId) {
      throw new TenantValidationError('Part belongs to different tenant');
    }

    return firestoreToPart(data, partDoc.id);

  } catch (error: any) {
    console.error(`❌ Error fetching part ${partId}:`, error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to fetch part',
      'FETCH_FAILED',
      { partId, error: error.message }
    );
  }
}

export async function getPartsList(
  options: PartListOptions = {}
): Promise<Part[]> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const constraints: QueryConstraint[] = [
      where('companyId', '==', companyId)
    ];

    if (options.category) {
      constraints.push(where('category', '==', options.category));
    }

    if (options.isActive !== undefined) {
      constraints.push(where('isActive', '==', options.isActive));
    }

    constraints.push(orderBy('name', 'asc'));

    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(
      collection(db, 'companies', companyId, 'parts'),
      ...constraints
    );

    const snapshot = await getDocs(q);
    
    let parts = snapshot.docs.map(doc => 
      firestoreToPart(doc.data(), doc.id)
    );

    // Client-side filters
    if (options.belowMinimum) {
      parts = parts.filter(part => part.quantityAvailable < part.minQuantity);
    }

    if (options.search) {
      const searchLower = options.search.toLowerCase();
      parts = parts.filter(part => 
        part.name.toLowerCase().includes(searchLower) ||
        part.partNumber.toLowerCase().includes(searchLower) ||
        part.description.toLowerCase().includes(searchLower) ||
        part.manufacturerPartNumber?.toLowerCase().includes(searchLower)
      );
    }

    return parts;

  } catch (error: any) {
    console.error('❌ Error fetching parts list:', error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to fetch parts list',
      'FETCH_LIST_FAILED',
      { error: error.message }
    );
  }
}

export async function updatePart(
  partId: string,
  updates: UpdatePartData,
  updatedBy: string
): Promise<void> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const existing = await getPartById(partId, companyId);
    
    if (existing.companyId !== companyId) {
      throw new TenantValidationError('Cannot update part from different tenant');
    }

    const partRef = doc(db, 'companies', companyId, 'parts', partId);

    const updateData: any = {
      updatedAt: Timestamp.now(),
      updatedBy
    };

    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) {
        updateData[key] = value;
      }
    });

    await updateDoc(partRef, updateData);


  } catch (error: any) {
    console.error(`❌ Error updating part ${partId}:`, error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to update part',
      'UPDATE_FAILED',
      { partId, error: error.message }
    );
  }
}

// ============================================================================
// TRANSACTION OPERATIONS
// ============================================================================

export async function createTransaction(
  data: CreateTransactionData,
  performedBy: string,
  performedByName: string
): Promise<string> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    return await runTransaction(db, async (transaction) => {
      // Get current part data
      const partRef = doc(db, 'companies', companyId, 'parts', data.partId);
      const partDoc = await transaction.get(partRef);
      
      if (!partDoc.exists()) {
        throw new PartNotFoundError(data.partId);
      }

      const part = firestoreToPart(partDoc.data(), partDoc.id);

      // Validate stock for issue transactions
      if (data.type === 'issue' || data.type === 'transfer' || data.type === 'sale' || data.type === 'scrap') {
        if (part.quantityAvailable < data.quantity) {
          throw new InsufficientStockError(data.partId, data.quantity, part.quantityAvailable);
        }
      }

      // Generate transaction number
      const transactionNumber = await generateTransactionNumber(companyId);
      const now = Timestamp.now();

      // Create transaction record
      const transactionData = {
        companyId,
        transactionNumber,
        type: data.type,
        partId: data.partId,
        partNumber: part.partNumber,
        partName: part.name,
        quantity: data.quantity,
        unitCost: data.unitCost,
        totalCost: data.quantity * data.unitCost,
        unitOfMeasure: part.unitOfMeasure,
        serialNumbers: data.serialNumbers || [],
        lotNumber: data.lotNumber || null,
        fromLocation: data.fromLocation || null,
        toLocation: data.toLocation || null,
        workOrderId: data.workOrderId || null,
        maintenanceId: data.maintenanceId || null,
        equipmentId: data.equipmentId || null,
        equipmentName: data.equipmentName || null,
        vendorId: data.vendorId || null,
        vendorName: data.vendorName || null,
        reason: data.reason || null,
        notes: data.notes || null,
        performedBy,
        performedByName,
        transactionDate: now,
        createdAt: now,
        createdBy: performedBy
      };

      const transactionRef = await addDoc(
        collection(db, 'companies', companyId, 'parts', data.partId, 'transactions'),
        transactionData
      );

      // Update part quantities
      let newQuantityOnHand = part.quantityOnHand;
      let newQuantityAllocated = part.quantityAllocated;
      const partUpdates: any = {
        updatedAt: now,
        updatedBy: performedBy
      };

      switch (data.type) {
        case 'receipt':
          newQuantityOnHand += data.quantity;
          partUpdates.quantityOnHand = newQuantityOnHand;
          partUpdates.totalReceived = part.totalReceived + data.quantity;
          partUpdates.lastReceivedDate = now;
          partUpdates.lastPurchasePrice = data.unitCost;
          // Update average cost
          const totalCost = (part.averageCost * part.quantityOnHand) + (data.unitCost * data.quantity);
          partUpdates.averageCost = totalCost / newQuantityOnHand;
          break;

        case 'issue':
        case 'sale':
        case 'scrap':
          newQuantityOnHand -= data.quantity;
          partUpdates.quantityOnHand = newQuantityOnHand;
          partUpdates.totalIssued = part.totalIssued + data.quantity;
          partUpdates.lastIssuedDate = now;
          break;

        case 'return':
          newQuantityOnHand += data.quantity;
          partUpdates.quantityOnHand = newQuantityOnHand;
          break;

        case 'reserve':
          newQuantityAllocated += data.quantity;
          partUpdates.quantityAllocated = newQuantityAllocated;
          break;

        case 'unreserve':
          newQuantityAllocated -= data.quantity;
          partUpdates.quantityAllocated = newQuantityAllocated;
          break;

        case 'adjustment':
          partUpdates.quantityOnHand = data.quantity; // Absolute value
          newQuantityOnHand = data.quantity;
          break;

        case 'transfer':
          // Quantity stays same, just location changes
          break;
      }

      // Calculate available quantity
      partUpdates.quantityAvailable = newQuantityOnHand - newQuantityAllocated;
      partUpdates.totalValue = newQuantityOnHand * part.averageCost;

      transaction.update(partRef, partUpdates);

      return transactionRef.id;
    });

  } catch (error: any) {
    console.error('❌ Error creating transaction:', error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to create transaction',
      'TRANSACTION_FAILED',
      { error: error.message }
    );
  }
}

export async function getTransactionHistory(
  options: TransactionListOptions = {}
): Promise<Transaction[]> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    if (options.partId) {
      // Get transactions for specific part
      const constraints: QueryConstraint[] = [
        orderBy('transactionDate', 'desc')
      ];

      if (options.limit) {
        constraints.push(firestoreLimit(options.limit));
      }

      const q = query(
        collection(db, 'companies', companyId, 'parts', options.partId, 'transactions'),
        ...constraints
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => firestoreToTransaction(doc.data(), doc.id));
    }

    // Get all transactions (not recommended for large datasets)
    const parts = await getPartsList();
    const allTransactions: Transaction[] = [];

    for (const part of parts) {
      const txnSnapshot = await getDocs(
        collection(db, 'companies', companyId, 'parts', part.id, 'transactions')
      );
      txnSnapshot.docs.forEach(doc => {
        allTransactions.push(firestoreToTransaction(doc.data(), doc.id));
      });
    }

    return allTransactions.sort((a, b) => {
      const dateAValue = a.transactionDate instanceof Date ? a.transactionDate : (a.transactionDate as any).toDate();
      const dateBValue = b.transactionDate instanceof Date ? b.transactionDate : (b.transactionDate as any).toDate();
      const dateA = new Date(dateAValue);
      const dateB = new Date(dateBValue);
      return dateB.getTime() - dateA.getTime();
    });

  } catch (error: any) {
    console.error('❌ Error fetching transaction history:', error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to fetch transaction history',
      'FETCH_TRANSACTIONS_FAILED',
      { error: error.message }
    );
  }
}

// ============================================================================
// ANALYTICS & REPORTING
// ============================================================================

export async function getInventoryStats(): Promise<InventoryStats> {
  try {
    const companyId = getCompanyId();
    validateCompanyContext(companyId);

    const parts = await getPartsList();

    const stats: InventoryStats = {
      totalParts: parts.length,
      totalValue: 0,
      totalQuantity: 0,
      belowMinimum: 0,
      outOfStock: 0,
      overstock: 0,
      byCategory: {},
      topValueParts: [],
      lowStockParts: []
    };

    parts.forEach(part => {
      stats.totalValue += part.totalValue;
      stats.totalQuantity += part.quantityOnHand;
      stats.byCategory[part.category] = (stats.byCategory[part.category] || 0) + 1;

      if (part.quantityAvailable < part.minQuantity) {
        stats.belowMinimum++;
        stats.lowStockParts.push(part);
      }

      if (part.quantityOnHand === 0) {
        stats.outOfStock++;
      }

      if (part.quantityOnHand > part.maxQuantity) {
        stats.overstock++;
      }
    });

    // Top 10 value parts
    stats.topValueParts = [...parts]
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 10);

    // Low stock (sorted by urgency)
    stats.lowStockParts = stats.lowStockParts
      .sort((a, b) => {
        const urgencyA = a.quantityAvailable / a.minQuantity;
        const urgencyB = b.quantityAvailable / b.minQuantity;
        return urgencyA - urgencyB;
      })
      .slice(0, 20);

    return stats;

  } catch (error: any) {
    console.error('❌ Error calculating inventory stats:', error);
    
    if (error instanceof PartsServiceError) {
      throw error;
    }

    throw new PartsServiceError(
      'Failed to calculate inventory statistics',
      'STATS_FAILED',
      { error: error.message }
    );
  }
}

export async function getPartsNeedingReorder(): Promise<Part[]> {
  const parts = await getPartsList();
  return parts.filter(part => 
    part.isActive && 
    part.quantityAvailable <= part.reorderPoint
  );
}

// Export service object
export const partsInventoryService = {
  createPart,
  getPartById,
  getPartsList,
  updatePart,
  createTransaction,
  getTransactionHistory,
  getInventoryStats,
  getPartsNeedingReorder
};

export default partsInventoryService;
